import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, TrendingDown } from 'lucide-react';
import { platformFetch } from '../../../platform/http';
import BalanceTrendChart, { type TrendChartPoint } from './BalanceTrendChart';

type TrendPoint = {
  date: string;
  total: number;
  sites: Record<string, number>;
};

type TrendResponse = {
  days: number;
  points: TrendPoint[];
};

type SiteOption = {
  key: string;
  name: string;
};

const RANGES = [7, 30, 90] as const;

function formatCny(value: number): string {
  return `¥${value.toFixed(2)}`;
}

export default function BalanceTrendPanel({ sites }: { sites: SiteOption[] }) {
  const [days, setDays] = useState<number>(30);
  const [siteKey, setSiteKey] = useState('all');
  const [data, setData] = useState<TrendResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const load = useCallback(async (range: number) => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await platformFetch(`/admin/upstream-balances/trend?days=${range}`);
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(payload?.error || '加载余额趋势失败');
      }
      setData(payload as TrendResponse);
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : '加载余额趋势失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(days);
  }, [days, load]);

  const chartPoints = useMemo<TrendChartPoint[]>(() => {
    const points = data?.points ?? [];
    if (siteKey === 'all') {
      return points.map((point) => ({ date: point.date, value: point.total }));
    }
    return points
      .filter((point) => typeof point.sites[siteKey] === 'number')
      .map((point) => ({ date: point.date, value: point.sites[siteKey] }));
  }, [data, siteKey]);

  const summary = useMemo(() => {
    if (chartPoints.length === 0) return null;
    const current = chartPoints[chartPoints.length - 1].value;
    if (chartPoints.length < 2) {
      return { current, dailyBurn: null as number | null, daysLeft: null as number | null };
    }
    const burn = (chartPoints[0].value - current) / (chartPoints.length - 1);
    return {
      current,
      dailyBurn: burn,
      daysLeft: burn > 0 ? Math.floor(current / burn) : null,
    };
  }, [chartPoints]);

  return (
    <div className="rounded-2xl border border-light-border bg-white px-4 py-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className="inline-flex items-center gap-1.5 text-sm font-medium text-light-text">
            <TrendingDown className="h-3.5 w-3.5 text-light-muted" />
            余额趋势
          </span>
          {summary && (
            <span className="text-xs text-light-muted">
              当前 <span className="font-medium text-light-text">{formatCny(summary.current)}</span>
              <span className="mx-1.5 text-light-border">|</span>
              日均{' '}
              {summary.dailyBurn == null
                ? '-'
                : summary.dailyBurn > 0
                  ? formatCny(summary.dailyBurn)
                  : '未在消耗'}
              <span className="mx-1.5 text-light-border">|</span>
              预计可用 {summary.daysLeft == null ? '—' : `${summary.daysLeft} 天`}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex overflow-hidden rounded-md border border-light-border">
            {RANGES.map((range) => (
              <button
                key={range}
                type="button"
                onClick={() => setDays(range)}
                className={`px-2 py-0.5 text-[11px] ${
                  days === range
                    ? 'bg-light-text text-white'
                    : 'text-light-muted hover:bg-light-hover hover:text-light-text'
                }`}
              >
                {range}天
              </button>
            ))}
          </div>
          <select
            value={siteKey}
            onChange={(event) => setSiteKey(event.target.value)}
            className="rounded-md border border-light-border bg-white px-1.5 py-0.5 text-[11px] text-light-muted focus:outline-none focus:ring-2 focus:ring-blue-200"
          >
            <option value="all">全部站点</option>
            {sites.map((site) => (
              <option key={site.key} value={site.key}>
                {site.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loadError && (
        <div className="mt-2 flex items-center justify-between rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          <span>{loadError}</span>
          <button type="button" onClick={() => void load(days)} className="underline">
            重试
          </button>
        </div>
      )}

      <div className="mt-1">
        {loading && !data ? (
          <div className="flex h-[132px] items-center justify-center text-xs text-light-muted">
            <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
            正在加载趋势...
          </div>
        ) : chartPoints.length === 0 ? (
          <div className="flex h-[132px] flex-col items-center justify-center gap-1 text-xs text-light-muted">
            <span>还没有历史数据</span>
            <span className="text-[11px]">每 30 分钟采样一次，打开本页也会补一个点，攒够两天就能看出趋势</span>
          </div>
        ) : (
          <BalanceTrendChart points={chartPoints} formatValue={formatCny} />
        )}
      </div>

      {chartPoints.length > 0 && (
        <div className="text-right text-[10px] text-light-muted">纵轴未从 0 开始，用于放大波动</div>
      )}
    </div>
  );
}
